import React, {useEffect, useLayoutEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Flex,
    SimpleGrid,
    Button,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import ProductTile, {Skeleton as ProductTileSkeleton} from '../../../components/product-tile'
import {renderLayoutsSwitch} from '../../../components/optimizely/utils/contentRenderer'
import ItemsCounter from './items-counter'
import ProductGridItemBoundary from './product-grid-item-boundary'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {mergeRefs} from '../../../utils/ref-utils'
import {
    PRODUCT_LIST_IMAGE_VIEW_TYPE,
    PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID
} from '@salesforce/retail-react-app/app/constants'
import {
    fillGDIResponsiveInheritance,
    hasGDIViewTypes,
    GDI_MEDIA_FILL_SX
} from '../../../components/optimizely/gdi/utils/gdi-styles'
import {parseResponsiveViewTypes} from '../../../components/optimizely/gdi/utils/gdi-responsive'
import {markExpContentExpanded} from '../../../utils/optimizely-exp-behaviour'

const ProductGrid = ({
    // Products
    allProducts,
    productSearchResult,
    skeletonPlaceholders,
    // Loading states
    isRefetching,
    isFetched,
    isCategoryChanging,
    searchParams,
    // Layout
    isAlternativeGridView,
    smallGridImageNumber,
    gridColumns,
    // CMS
    currentBreakpoint,
    isDesktopBreakpoint,
    desktopGroupSize,
    gridInterruptorPositions,
    // Enrichment / tracking
    enrichProduct,
    arePricesReady = true,
    observeProduct,
    productRefs,
    prependScrollAnchor,
    // Wishlist
    wishlist,
    // Handlers
    addItemToWishlist,
    removeItemFromWishlist,
    handleProductClick,
    // Pagination
    total,
    minPageLoaded,
    maxPageLoaded,
    goToPage,
    isLoadingNewPage,
    // Variable per-page limits
    getLimitForPage,
    loadedLimitTotal
}) => {
    const {formatMessage} = useIntl()
    const pgStyles = useMultiStyleConfig('ProductGrid')

    // Keep the first pre-prepend product visually fixed as earlier pages prepend
    // above it. Runs ONLY when the prepended content changes (not on unrelated
    // re-renders), and corrects RELATIVE to the anchor's recorded position — so it
    // never fights manual scrolling and never targets an absolute Y (no jump-to-top).
    // After scrollBy the anchor sits back at anchorTop, so the next commit measures
    // only its own incremental growth; anchorTop stays constant.
    // Invariant: every above-viewport element reserves its height before paint
    // (AspectRatio tiles, padding-top interruptor spacers); async height changes above
    // the anchor without an allProducts/skeleton change would not be compensated.
    useLayoutEffect(() => {
        const anchor = prependScrollAnchor?.current
        if (!anchor?.anchorId) return
        const el = productRefs.current[anchor.anchorId]
        if (!el) return
        const delta = el.getBoundingClientRect().top - anchor.anchorTop
        if (delta !== 0) window.scrollBy(0, delta)
    }, [allProducts, skeletonPlaceholders, prependScrollAnchor, productRefs])

    // Clear the anchor only once the whole prepend chain is idle — not mid-chain,
    // or the second extend-down prepend would go uncompensated. Boolean only; no scroll.
    useEffect(() => {
        if (!prependScrollAnchor?.current) return
        const hasPrependSkeletons = skeletonPlaceholders?.some((s) => s.position === 'prepend')
        const groupSettled =
            !isDesktopBreakpoint ||
            desktopGroupSize <= 1 ||
            ((minPageLoaded - 1) % desktopGroupSize === 0 &&
                maxPageLoaded % desktopGroupSize === 0)
        if (!hasPrependSkeletons && !isLoadingNewPage && groupSettled) {
            prependScrollAnchor.current = null
        }
    }, [
        allProducts,
        skeletonPlaceholders,
        isLoadingNewPage,
        minPageLoaded,
        maxPageLoaded,
        isDesktopBreakpoint,
        desktopGroupSize,
        prependScrollAnchor
    ])

    const products =
        allProducts.length > 0 ? allProducts : productSearchResult?.hits

    // gridInterruptorPositions is `{ position: GDIBlock[] }` already filtered to
    // the active category & current breakpoint by the parent hook.
    const interruptorPositions = gridInterruptorPositions || {}

    /* Per-breakpoint column count of the product grid (mirrors
     * `gridColumns = [2, 2, 4, 4]` in use-product-list.js → base/sm/md/lg).
     * Used to cap an interruptor's authored ColSpan so a 2-wide tile inside
     * a 2-column mobile grid still fits, and so slot accounting knows how
     * many product slots the interruptor displaces. */
    const BREAKPOINT_TO_GRID_COLS = {
        mobile: gridColumns?.[0] ?? 2,
        tablet: gridColumns?.[2] ?? gridColumns?.[1] ?? 4,
        desktop: gridColumns?.[3] ?? gridColumns?.[2] ?? 4,
        xl: gridColumns?.[3] ?? 4
    }
    const maxGridCols = BREAKPOINT_TO_GRID_COLS[currentBreakpoint] ?? 4

    /* Read the authored ColSpan on a GDIContentTileBlock-based interruptor
     * for the current breakpoint. Returns 1 (1x1) by default; honours 2x1
     * when authored. Capped at the parent grid's column count. */
    const getInterruptorColSpan = (block) => {
        if (block?.__typename !== 'GDIContentTileBlock') return 1
        const stylesArr = block.GDIContentTileBlockStylesPerViewType
        if (!Array.isArray(stylesArr)) return 1
        const targetBreakpoint =
            currentBreakpoint === 'desktop' || currentBreakpoint === 'xl'
                ? 'lg'
                : currentBreakpoint === 'tablet'
                ? 'md'
                : 'base'
        const spanByBreakpoint = {}

        const setSpan = (entry, breakpoints) => {
            const n = Number(entry?.ColSpan)
            if (!Number.isFinite(n) || n <= 0) return
            breakpoints.forEach((bp) => {
                spanByBreakpoint[bp] = n
            })
        }

        stylesArr.filter((entry) => !hasGDIViewTypes(entry)).forEach((entry) => {
            setSpan(entry, ['base', 'md', 'lg'])
        })

        stylesArr.filter((entry) => hasGDIViewTypes(entry)).forEach((entry) => {
            setSpan(entry, parseResponsiveViewTypes(entry.ViewTypesToApply))
        })

        const inheritedSpan = fillGDIResponsiveInheritance(spanByBreakpoint)

        return Math.min(inheritedSpan[targetBreakpoint] ?? 1, maxGridCols)
    }

    // Each GDI interruptor child occupies as many grid cells as its authored
    // ColSpan (defaulting to 1). The block of children at a given position
    // contributes the sum of their spans.
    const getInterruptorSlotCount = (position) => {
        const blocks = interruptorPositions[position]
        if (!Array.isArray(blocks)) return 0
        return blocks.reduce((acc, b) => acc + getInterruptorColSpan(b), 0)
    }

    // Analytics click position should count each interruptor block once, not by
    // its rendered column span in the grid.
    const getInterruptorAnalyticsOffset = (position) => {
        const blocks = interruptorPositions[position]
        return Array.isArray(blocks) ? blocks.length : 0
    }

    // Interruptors are authored at absolute positions in the real product ordering
    // (position 1 = the very first product). When the loaded range starts past page 1
    // (deep link / back-nav), offset the lookup by the first loaded product's absolute
    // index so an interruptor only appears once its real neighbouring products load.
    const firstLoadedOffset = Math.max(0, (minPageLoaded - 1) * (searchParams.limit || 0))

    // Cap rendering so each loaded page shows exactly its page-size worth of grid
    // items (products + interruptors). Once all products are fetched, remove the cap.
    const allFetched = (products?.length ?? 0) >= total
    const maxGridSlots = allFetched ? Infinity : (loadedLimitTotal ?? maxPageLoaded * searchParams.limit)
    let visibleProductCount = 0
    let gridSlotCount = firstLoadedOffset
    if (products) {
        for (let i = 0; i < products.length; i++) {
            if (gridSlotCount >= maxGridSlots) break
            const interruptorSlots = getInterruptorSlotCount(firstLoadedOffset + i + 1)
            const slotsNeeded = interruptorSlots + 1
            if (gridSlotCount + slotsNeeded > maxGridSlots) break
            gridSlotCount += slotsNeeded
            visibleProductCount++
        }
    }
    const visibleProducts = products?.slice(0, visibleProductCount) || []

    // hasMore is true only when there are more products *after* the currently
    // loaded pages (i.e. the next-page offset is within the total count). Using
    // loadedLimitTotal (= getOffsetForPage(maxPageLoaded + 1)) rather than
    // visibleProductCount prevents "Show More" from appearing after back-
    // navigation when the restored page is the last one and no further pages exist.
    const hasMore = total != null && (loadedLimitTotal ?? 0) < total

    // On desktop the view is a group of pages (e.g. a pair); only offer "Show More"
    // once the current group is complete (ends on a group boundary) and settled.
    const groupComplete =
        !isDesktopBreakpoint || desktopGroupSize <= 1 || maxPageLoaded % desktopGroupSize === 0
    const showShowMore = hasMore && !isLoadingNewPage && groupComplete

    return (
        <Box>
            <SimpleGrid
                columns={gridColumns}
                spacingX={2}
                spacingY={isAlternativeGridView ? 0 : 6}
            >
                {isHydrated() &&
                ((isRefetching && !allProducts?.length) ||
                    !productSearchResult ||
                    isCategoryChanging) ? (
                    new Array(searchParams.limit)
                        .fill(0)
                        .map((value, index) => <ProductTileSkeleton key={index} />)
                ) : (
                    <>
                        {skeletonPlaceholders
                            ?.filter((s) => s.position === 'prepend')
                            .map((s) => (
                                <ProductTileSkeleton key={s.id} />
                            ))}

                        {(() => {
                            const itemsToRender = []
                            let analyticsPosition = firstLoadedOffset
                            visibleProducts?.forEach((productSearchItem, index) => {
                                // productPosition is 1-based for interruptor lookup (interruptors are authored at positions 1, 2, 3...)
                                const productPosition = firstLoadedOffset + index + 1
                                analyticsPosition += getInterruptorAnalyticsOffset(productPosition)
                                // gtmIndex is 0-based for GTM events (0, 1, 2...)
                                const gtmIndex = firstLoadedOffset + index
                                const productId = productSearchItem.productId
                                const isInWishlist =
                                    !!wishlist?.customerProductListItems?.find(
                                        (item) => item.productId === productId
                                    )

                                if (interruptorPositions[productPosition] && !isAlternativeGridView) {
                                    interruptorPositions[productPosition].forEach((block, blockIndex) => {
                                        const interruptorSpan = getInterruptorColSpan(block)
                                        // Media-only blocks have no text layer, so the fill styles can target the whole subtree.
                                        const isMediaOnlyBlock =
                                            block?.__typename === 'GDIImageBlock' ||
                                            block?.__typename === 'GDIVideoBlock'
                                        itemsToRender.push(
                                            <ProductGridItemBoundary
                                                key={`grid-interruptor-${productPosition}`}
                                                resetKey={`interruptor-${productPosition}-${currentBreakpoint}`}
                                                errorContext={{
                                                    itemType: 'gridInterruptor',
                                                    position: productPosition,
                                                    breakpoint: currentBreakpoint
                                                }}
                                            >
                                                <Box
                                                    key={`grid-interruptor-${productPosition}-${blockIndex}`}
                                                    data-testid={`grid-interruptor-${productPosition}`}
                                                    /* Span the parent SimpleGrid by the
                                                     * authored ColSpan so a 2x1 inter-
                                                     * ruptor occupies two product slots
                                                     * horizontally. This Box is the
                                                     * direct grid child (the boundary
                                                     * just passes children through), so
                                                     * the span has to live here. */
                                                    gridColumn={
                                                        interruptorSpan > 1
                                                            ? `span ${interruptorSpan}`
                                                            : undefined
                                                    }
                                                    /* Pixel-perfect: match the product
                                                     * tile IMAGE height (image Chakra
                                                     * AspectRatio is locked to 19/28 via
                                                     * theme `mediaAspect.ratio`).
                                                     *
                                                     * Target height = colWidth * 28/19.
                                                     * Wrapper width W = N*col + (N-1)*8.
                                                     * Therefore:
                                                     *   height = W*(28/(19N)) - (N-1)*8*(28/(19N))
                                                     *
                                                     * CSS can't express that via
                                                     * `aspect-ratio`, but the legacy
                                                     * padding-top trick can — percentage
                                                     * padding is resolved against parent
                                                     * width, so on a `width: 100%` child
                                                     * it equals percent * wrapper_width.
                                                     * Inner content is absolutely
                                                     * positioned to fill the spacer.
                                                     * `alignSelf:start` stops CSS Grid
                                                     * from stretching the wrapper to the
                                                     * (taller) row height. */
                                                    alignSelf="start"
                                                    position="relative"
                                                    width="100%"
                                                    paddingTop={
                                                        interruptorSpan === 1
                                                            ? '147.3684%' /* 28/19 */
                                                            : `calc(${
                                                                  (28 / (19 * interruptorSpan)) *
                                                                  100
                                                              }% - ${
                                                                  (((interruptorSpan - 1) * 8 * 28) /
                                                                      (19 * interruptorSpan)
                                                                  ).toFixed(4)
                                                              }px)`
                                                    }
                                                >
                                                    <Box
                                                        position="absolute"
                                                        inset={0}
                                                        overflow="hidden"
                                                        sx={
                                                            isMediaOnlyBlock
                                                                ? GDI_MEDIA_FILL_SX
                                                                : undefined
                                                        }
                                                    >
                                                        {renderLayoutsSwitch(block, {
                                                            position: productPosition,
                                                            breakpoint: currentBreakpoint,
                                                            /* Signal to
                                                             * GDIContentTileBlock that it
                                                             * should size to the
                                                             * SimpleGrid row (= product
                                                             * tile image height) rather
                                                             * than imposing its authored
                                                             * aspect ratio. */
                                                            isGridInterruptor: true
                                                        })}
                                                    </Box>
                                                </Box>
                                            </ProductGridItemBoundary>
                                        )
                                    })
                                }

                                itemsToRender.push(
                                    <ProductGridItemBoundary
                                        key={productSearchItem.productId}
                                        resetKey={`${productSearchItem.productId}-${isAlternativeGridView ? 'alternative' : 'default'}`}
                                        fallback={<ProductTileSkeleton />}
                                        errorContext={{
                                            itemType: 'productTile',
                                            productId,
                                            gridView: isAlternativeGridView ? 'alternative' : 'default'
                                        }}
                                    >
                                        <ProductTile
                                            showScrollHint
                                            data-testid={`sf-product-tile-${productSearchItem.productId}`}
                                            data-product-id={productSearchItem.productId}
                                            ref={mergeRefs((el) => {
                                                if (el) productRefs.current[productId] = el
                                            }, observeProduct)}
                                            product={enrichProduct(productSearchItem)}
                                            isPriceLoading={!arePricesReady}
                                            enableFavourite={!isAlternativeGridView}
                                            isFavourite={isInWishlist}
                                            isRefreshingData={isRefetching && isFetched}
                                            imageViewType={PRODUCT_LIST_IMAGE_VIEW_TYPE}
                                            mainImageNumber={
                                                isAlternativeGridView ? smallGridImageNumber : undefined
                                            }
                                            selectableAttributeId={PRODUCT_LIST_SELECTABLE_ATTRIBUTE_ID}
                                            showSwatches={!isAlternativeGridView}
                                            showVideo={!isAlternativeGridView}
                                            showQV={!isAlternativeGridView}
                                            showDetails={!isAlternativeGridView}
                                            onClick={(tileClick) => {
                                                handleProductClick({
                                                    productSearchItem:
                                                        tileClick?.productSearchItem ??
                                                        productSearchItem,
                                                    productId: tileClick?.productId ?? productId,
                                                    index: gtmIndex
                                                })
                                            }}
                                            onQuickViewOpen={() => {
                                                try {
                                                    window.sessionStorage.setItem(
                                                        'plp-productContext',
                                                        JSON.stringify({productId, index: gtmIndex})
                                                    )
                                                } catch {}
                                            }}
                                            onFavouriteToggle={(toBeFavourite) => {
                                                const action = toBeFavourite
                                                    ? addItemToWishlist
                                                    : removeItemFromWishlist
                                                return action(productSearchItem, index)
                                            }}
                                            dynamicImageProps={{
                                                widths: ['50vw', '50vw', '25vw', '25vw', '25vw'],
                                                imageProps: {
                                                    loading: index < 4 ? 'eager' : 'lazy',
                                                    fetchpriority: index === 0 ? 'high' : 'auto',
                                                    decoding: index < 4 ? 'sync' : 'async'
                                                }
                                            }}
                                            videoProps={{
                                                preload: index < 4 ? 'auto' : 'none'
                                            }}
                                        />
                                    </ProductGridItemBoundary>
                                )

                                analyticsPosition += 1
                            })
                            return itemsToRender
                        })()}

                        {skeletonPlaceholders
                            ?.filter((s) => s.position === 'append')
                            .map((s) => (
                                <ProductTileSkeleton key={s.id} />
                            ))}
                    </>
                )}
            </SimpleGrid>

            {showShowMore && (
                <Flex flexDirection="column" alignItems="center" gap={8} {...pgStyles.showMoreContainer}>
                    <ItemsCounter
                        loadedCount={visibleProductCount}
                        totalCount={total}
                    />
                    <Button
                        variant="webPrimary"
                        onClick={() => {
                            markExpContentExpanded()
                            goToPage(maxPageLoaded + 1)
                        }}
                        {...pgStyles.showMoreButton}
                    >
                        {formatMessage({id: 'plp.button.show.more', defaultMessage: 'Show More'})}
                    </Button>
                </Flex>
            )}
        </Box>
    )
}

ProductGrid.propTypes = {
    allProducts: PropTypes.array.isRequired,
    productSearchResult: PropTypes.object,
    skeletonPlaceholders: PropTypes.array.isRequired,
    isRefetching: PropTypes.bool,
    isFetched: PropTypes.bool,
    isCategoryChanging: PropTypes.bool,
    searchParams: PropTypes.object.isRequired,
    isAlternativeGridView: PropTypes.bool,
    smallGridImageNumber: PropTypes.number,
    gridColumns: PropTypes.array.isRequired,
    currentBreakpoint: PropTypes.string,
    isDesktopBreakpoint: PropTypes.bool,
    desktopGroupSize: PropTypes.number,
    gridInterruptorPositions: PropTypes.object,
    enrichProduct: PropTypes.func.isRequired,
    arePricesReady: PropTypes.bool,
    observeProduct: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    productRefs: PropTypes.object.isRequired,
    prependScrollAnchor: PropTypes.object,
    wishlist: PropTypes.object,
    addItemToWishlist: PropTypes.func.isRequired,
    removeItemFromWishlist: PropTypes.func.isRequired,
    handleProductClick: PropTypes.func.isRequired,
    total: PropTypes.number,
    page: PropTypes.number,
    minPageLoaded: PropTypes.number,
    maxPageLoaded: PropTypes.number,
    goToPage: PropTypes.func.isRequired,
    isLoadingNewPage: PropTypes.bool,
    isSearch: PropTypes.bool,
    getLimitForPage: PropTypes.func,
    loadedLimitTotal: PropTypes.number
}

export default ProductGrid
